import ButtonT from '@components/tailwind-components/Button/ButtonT';
import SelectInputWithLabelT from '@components/tailwind-components/Input/SelectInputWithLabelT';
import { NON_TARGETABLE_BLOCK_TYPES } from '@constants/flow';
import type { TBlockConfig } from '@conversed/shared/src/central/types/block.type';
import type {
  Gate,
  TGateInitForm,
} from '@conversed/shared/src/central/types/blocks/Gate';
import { useBlocksUpdates } from '@hooks/flow/block/useBlocksUpdates';
import { useFlow } from '@hooks/flow/flow/useFlow';
import { t } from 'i18next';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useModal } from '../../../hooks/modal/useModal';
import { useMainSelector } from '../../../hooks/useMainSelector';

export const GateSelectSubflowModal: React.FC<{ block: Gate }> = ({
  block,
}) => {
  const { hideModal } = useModal();
  const { subFlows, selectedSubFlow } = useFlow();
  const { updateBlocks } = useBlocksUpdates();

  const filteredSubFlows = useMemo(() => {
    return subFlows.filter((subFlow) => subFlow !== selectedSubFlow);
  }, [selectedSubFlow, subFlows]);

  const [availableSubflowBlocks, setAvailableSubflowBlocks] =
    useState<TBlockConfig[]>();

  const {
    ui: { activeModal },
  } = useMainSelector();

  const {
    designer: { blocks },
  } = useMainSelector();

  const onSubmitTextBlock = useCallback(
    (gate: TGateInitForm) => {
      if (gate.routeToSubFlow) {
        updateBlocks([
          {
            ...block,
            ...gate,
            destination:
              typeof gate.destination === 'string'
                ? [gate.destination]
                : gate.destination,
          },
        ]);
        hideModal();
      }
    },
    [updateBlocks, hideModal, block],
  );

  const { handleSubmit, control } = useForm({
    defaultValues: {
      routeToSubFlow: '',
      destination: [''],
    },
    shouldUseNativeValidation: true,
  });

  const populateBlocksDropdown = useCallback(
    (subflow: string) => {
      if (!blocks) {
        return;
      }

      // * Any block in the target subflow is a valid landing point - the gate
      // * handler enters the subflow there instead of at its entry block -
      // * except the types that cannot be landed on at all.
      const filteredBlocks = blocks.filter(
        (block) =>
          block.subFlow === subflow &&
          !NON_TARGETABLE_BLOCK_TYPES.includes(block.type),
      );
      setAvailableSubflowBlocks(filteredBlocks);
    },
    [blocks],
  );

  return (
    <form
      className='tailwind-modal-form'
      onSubmit={handleSubmit(onSubmitTextBlock)}
      id={activeModal?.title}>
      <div className='tailwind-modal-body'>
        <Controller
          name='routeToSubFlow'
          control={control}
          render={({ field: { value, onChange, name } }) => {
            return (
              <SelectInputWithLabelT
                field={name}
                label={t('flow.form.description.sub-flow')}
                control={control}
                onChange={(e) => {
                  populateBlocksDropdown(e.target.value);
                  onChange(e.target.value);
                }}
                values={filteredSubFlows.map((value) => ({
                  value: value,
                  description: value,
                }))}
              />
            );
          }}
        />

        {availableSubflowBlocks && availableSubflowBlocks.length > 0 && (
          <SelectInputWithLabelT
            field='destination'
            label={t('flow.form.select-destination-for-entry-block')}
            control={control}
            values={availableSubflowBlocks.map((item) => ({
              value: item.id,
              description: item.blockTitle ? item.blockTitle : item.id,
            }))}
          />
        )}
      </div>
      <div className='tailwind-modal-footer'>
        <ButtonT
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'
          onClick={() => {
            hideModal();
          }}>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT
          type='submit'
          styling='primary'
          size='xs'
          rounding='md'
          disabled={!filteredSubFlows?.length}>
          {t('general.buttons.save')}
        </ButtonT>
      </div>
    </form>
  );
};
